import { createElement, createContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events'
import { useDidMountEffect } from '~/utils/hooks'
import { fetchLocationInfo, getUser, getUserVerificationToken, getBookings, getStatus, hasToken, getAnonymousBookings, getRegions } from '~/lib/api'
import { getUserAccounts } from '~/lib/piq'
import { hasEnded } from '~/utils/data'
import i18next from '~/i18n'
import GlobalModal from '~/components/ui/GlobalModal'
import MarketingConsentModal from '~/components/MarketingConsentModal'
import AddPaymentMethodModal from '~/components/AddPaymentMethodModal'
import { showModal } from '~/lib/modal'
import { track } from '~/utils/gtm'

export const StoreContext = createContext()

const fetchInterval = 10 // Number of seconds between fetches of bookings / statuses

const config = {
  termsLink: i18next.language === 'sv' ? '/sv/hjalpcenter/boka/resevillkor/' : '/en/helpdesk/to-book/conditions-of-travel/',
  paymentTermsLink: i18next.language === 'sv' ? '/sv/hjalpcenter/boka/betalningsvillkor/' : '/en/helpdesk/to-book/payment-terms/'
}

const defaultStore = {
  user: null, bookings: null, paymentAccounts: null, preferredPaymentAccount: false, preferredPaymentAccountCompany: false, preferredPaymentMethod: false, preferredPaymentMethodCompany: false, isCompanyTrip: false, regions: null
}

const readStore = () => {
  const item = localStorage.getItem('reactStore')
  if (item) return JSON.parse(item)
  return defaultStore
}

const writeStore = (store) => {
  const item = JSON.stringify(store)
  localStorage.setItem('reactStore', item)
}


// `nested`: a second Store mounted on a page that already has one (the dynamic booking
// overlay in modern/index.js). It must not render the page-level singletons again: the
// global modal would open twice, and a modal raised while the overlay is being removed
// (the return trip offer) would vanish with it. The page's own instances serve the overlay.
export default function Store({ apps = [], children, nested = false }) {
  // Read store from local storage on app init
  const [store, setStore] = useState(readStore())
  const [sessionStore, setSessionStore] = useState({})
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
  const storeRef = useRef(store)
  const intervalRef = useRef(null)
  const pollingTypeRef = useRef(null) // 'status' or 'active'
  const fetchRef = useRef(true)
  const bookingsRef = useRef(null) // Keep track of latest bookings for polling
  const isFirstLoadRef = useRef(true) // true during page-load loginAndFetchBookings, false after
  const pendingAddPaymentModalRef = useRef(false) // set after fresh login with no TSAB accounts; cleared by PIQ check

  const setUser = user => {
    setStore(state => ({ ...state, user }))
  }

  const setBookings = bookings => {
    bookingsRef.current = bookings // Update ref for polling callbacks
    setStore(state => ({ ...state, bookings }))
  }

  const setPaymentAccounts = paymentAccounts => {
    setStore(state => ({ ...state, paymentAccounts }))
  }

  const setPreferredPaymentAccount = (account, isCompanyTrip, paymentMethod = null) => {
    if (isCompanyTrip) {
      setStore(state => ({
        ...state,
        preferredPaymentAccountCompany: account,
        ...(paymentMethod ? { preferredPaymentMethodCompany: paymentMethod } : {})
      }))
    } else {
      setStore(state => ({
        ...state,
        preferredPaymentAccount: account,
        ...(paymentMethod ? { preferredPaymentMethod: paymentMethod } : {})
      }))
    }
  }

  const setCurrentPaymentAccount = (account, isCompanyTrip, paymentMethod = null) => {
      const s = { ...sessionStore };
      if(isCompanyTrip) {
        s.currentPaymentAccountCompany = account;
        if(paymentMethod) s.currentPaymentMethodCompany = paymentMethod;
      } else {
        s.currentPaymentAccount = account;
        if(paymentMethod) s.currentPaymentMethod = paymentMethod;
      }
      setSessionStore(s);
  }

  const setRegions = regions => {
    setStore(state => ({ ...state, regions }))
  }

  const setIsCompanyTrip = company => {
    setStore(state => ({ ...state, isCompanyTrip: company }))
  }

  // const updateBookings = bookings => {
  //   setStore(state => {
  //     const updated = bookings.map(booking => {
  //       return { ...booking, ...state.bookings?.find(item => item.id === booking.id) }
  //     })
  //     return { ...state, updated }
  //   })
  // }

  const isLoggedIn = () => {
    return hasToken() && store?.user
  }

  const loginUser = (userId, fresh = false) => {
    console.log('=== loginUser')
    getUser(userId).then((response) => {
      console.log(response)
      setUser(response)
      // If fresh login and no TSAB/TCA accounts, prime the pending flag — PIQ result will confirm or cancel
      if (fresh && (!response.accounts || response.accounts.length === 0)) {
        pendingAddPaymentModalRef.current = true
      }
      getPaymentAccounts(response, fresh)
      // getUserRegions()
    })
    .catch((error) => {
      console.error(error.response)
      if (error.response.error === 'USER_DOES_NOT_EXIST') {
        emitCustomEvent('UNAUTHORIZED')
      }
    })
  }

  const logoutUser = () => {
    console.log('Logging out user...')
    // stop polling
    clearInterval(intervalRef.current)
    pollingTypeRef.current = null
    // Clear all localStorage items
    localStorage.clear()
    // Clear sessionStorage as well
    sessionStorage.clear()
    // Reload the page to ensure all state is cleared
    console.log('Cleared storage, reloading page...')
    window.location.href = '/'
  }

  const refreshUser = () => {
    if (store?.user?.userId) {
      loginUser(store?.user?.userId)
    }
  }

  const getPaymentAccounts = (user, fresh = false) => {
    getUserVerificationToken(user.userId).then((userVerificationToken) => {
      console.log('userVerificationToken:', userVerificationToken)
      getUserAccounts(user.userId, userVerificationToken).then((response) => {
        console.log(response)
        // Filter out Apple Pay cards
        if (response.accounts?.length > 0) {
          response.accounts = response.accounts.filter(item => item.type !== 'applepay')
        }
        setPaymentAccounts(response.accounts)
        // Resolve the pending modal flag: show modal if no credit cards, cancel if user has cards
        if (pendingAddPaymentModalRef.current) {
          pendingAddPaymentModalRef.current = false
          if (!response.accounts || response.accounts.length === 0) {
            setShowAddPaymentModal(true)
          }
        }
        const latestStore = storeRef.current
        if (latestStore?.preferredPaymentAccount && latestStore.preferredPaymentAccount !== false) verifyAccount(latestStore.preferredPaymentAccount, false, user?.accounts, response.accounts, setPreferredPaymentAccount)
        if (latestStore?.preferredPaymentAccountCompany && latestStore.preferredPaymentAccountCompany !== false) verifyAccount(latestStore.preferredPaymentAccountCompany, true, user?.accounts, response.accounts, setPreferredPaymentAccount)
        if (sessionStore?.currentPaymentAccount && sessionStore?.currentPaymentAccount !== false) verifyAccount(sessionStore.currentPaymentAccount, false, user?.accounts, response.accounts, setCurrentPaymentAccount)
        if (sessionStore?.currentPaymentAccountCompany && sessionStore?.currentPaymentAccountCompany !== false) verifyAccount(sessionStore.currentPaymentAccountCompany, true, user?.accounts, response.accounts, setCurrentPaymentAccount)
      })
      .catch((error) => {
        console.error(error)
        // PIQ failed — show modal if pending (fresh login, no TSAB accounts)
        if (pendingAddPaymentModalRef.current) {
          pendingAddPaymentModalRef.current = false
          setShowAddPaymentModal(true)
        }
      })
    })
    .catch((error) => {
      console.error(error)
      // getUserVerificationToken failed — show modal if pending
      if (pendingAddPaymentModalRef.current) {
        pendingAddPaymentModalRef.current = false
        setShowAddPaymentModal(true)
      }
    })
  }

  const verifyAccount = (preferred, isCompanyTrip, accounts, paymentAccounts, setPaymentAccount) => {
    // Verify that the preferred account in store stil is available, otherwise set preferred account to false
    if(!preferred) return;
    if ((preferred.type === 'creditcard' && !paymentAccounts.some(item => item.accountId === preferred?.accountId)) 
      || ((preferred.type === 'TSAB' || preferred.type === 'TCA') && !accounts.some(item => item.accountNumber === preferred?.accountNumber))) {
      console.log('The preferred account does NOT exist.')
      setPaymentAccount(false, isCompanyTrip )
    }
    else {
      console.log('The preferred account DOES exist.')
    }
  }
  
  const getUserRegions = () => {
    getRegions().then((response) => {
      console.log(response)
      if (response?.regions?.length > 0) {
        setRegions(response.regions)
      }
    })
    .catch((error) => {
      console.error(error)
    })
  }

  // helper to start (or restart) polling using a single interval
  const startPolling = (type, fn) => {
    // if the same type is already polling we can just keep going
    if (pollingTypeRef.current === type && intervalRef.current) return
    clearInterval(intervalRef.current)
    intervalRef.current = setInterval(fn, 1000 * fetchInterval)
    pollingTypeRef.current = type
  }

  const getActiveBookings = async (userId) => {
    console.log('=== getActiveBookings')
    const id = userId || store?.user?.userId
    if (id) {
      try {
        // we no longer clear the interval here early; polling is managed centrally by startPolling
        let bookings = await getBookings(id)

        // Try to fetch any anonymous bookings made with the old API.
        // This should be removed when the site has been live for some time.
        if (localStorage?.orders) {
          try {
            let orders = JSON.parse(localStorage.orders)
            if (orders?.length > 0) {
              // Fetch anonymous bookings
              let anonBookings = await getAnonymousBookings(orders)

              // Skip ended bookings, add client booking token to the bookings to be able to fetch status
              anonBookings = anonBookings
                .filter(booking => !hasEnded(booking))
                .map(booking => {
                  const clientBookingToken = orders.find(item => item.orderId === booking.id)?.clientBookingToken
                  if (clientBookingToken) booking.clientBookingToken = clientBookingToken
                  return booking
                })

              // Concat and sort the bookings
              if (anonBookings?.length > 0) {
                bookings = [...bookings, ...anonBookings].sort((a, b) => a.pickUpTimeStamp < b.pickUpTimeStamp ? -1 : 1)
              }
            }
          }
          catch (e) {
            console.error(e)
          }
        }

        try {
          bookings = await getOriginLocationInfo(bookings)
        }
        catch (error) {
          console.error(error.response)
        }
        setBookings(bookings)

        // Check if any pending credit card booking has been confirmed
        try {
          const pendingBookings = JSON.parse(localStorage.getItem('pendingCreditCardBookings') || '[]')
          const isFirstPoll = !localStorage.getItem('hasPolledOnce')

          if (pendingBookings.length > 0) {
            if (bookings?.length > 0) {
              // Check if any pending bookingID is now in active bookings
              // statusCode 190 means the booking/payment FAILED — do not treat as confirmed
              const confirmedPending = pendingBookings.filter(pendingId =>
                bookings.some(booking => booking.id === pendingId && booking?.bookingStatus?.statusCode !== 190)
              )

              const failedPending = pendingBookings.filter(pendingId =>
                bookings.some(booking => booking.id === pendingId && booking?.bookingStatus?.statusCode === 190)
              )

              if (confirmedPending.length > 0) {
                // Emit event to navigate to booking confirmed step
                // The booking module will listen for this and navigate if it has the matching bookingID
                confirmedPending.forEach(bookingId => {
                  emitCustomEvent('PENDING_BOOKING_CONFIRMED', { bookingId })
                })
              }

              if (failedPending.length > 0) {
                failedPending.forEach(bookingId => {
                  emitCustomEvent('PENDING_BOOKING_FAILED', { bookingId })
                })
              }

              const handledIds = [...confirmedPending, ...failedPending]
              if (handledIds.length > 0) {
                const updatedPending = pendingBookings.filter(id => !handledIds.includes(id))
                localStorage.setItem('pendingCreditCardBookings', JSON.stringify(updatedPending))
              }
            }

            // If this is the first poll and booking not confirmed, emit event to enable button
            if (isFirstPoll && pendingBookings.length > 0) {
              localStorage.setItem('hasPolledOnce', 'true')
              pendingBookings.forEach(bookingId => {
                // Only emit if booking is NOT confirmed (not in active bookings)
                if (!bookings?.some(booking => booking.id === bookingId)) {
                  emitCustomEvent('FIRST_POLL_COMPLETE', { bookingId })
                }
              })
            }
          } else {
            // No pending bookings, clear the poll flag
            localStorage.removeItem('hasPolledOnce')
          }
        } catch (error) {
          console.error('Error checking pending bookings:', error)
        }

        // choose which polling behaviour we need based on current bookings
        if (bookings?.length > 0) {
          startPolling('status', () => {
            // NOTE: Every other call, fetch all bookings
            // Use bookingsRef to get the latest bookings, avoiding stale closures
            if (fetchRef.current) getBookingStatus(bookingsRef.current || [])
            else getActiveBookings(id)
            fetchRef.current = !fetchRef.current
          })
        } else {
          startPolling('active', () => getActiveBookings(id))
        }
      }
      catch (error) {
        console.error(error.response)
      }
    }
  }

  // Fetch location information for origin
  const getOriginLocationInfo = async (bookings) => {
    return Promise.all(bookings.map(async (booking) => {
      const response = await fetchLocationInfo(booking.originLocation)
      const locInfo = response?.locationInformation || []
      const hasNewTags = locInfo.some(item =>
        item.tag === 'DEPARTURE_INFO_PREBOOK' || item.tag === 'DEPARTURE_INFO_DIRECT'
      )
      const tag = hasNewTags
        ? (booking.bookingType === 'PRE_BOOKING' ? 'DEPARTURE_INFO_PREBOOK' : 'DEPARTURE_INFO_DIRECT')
        : 'DEPARTURE_INFO'
      const info = locInfo.filter(item => item.tag === tag).map(item => item.message)
      // Return new object instead of mutating
      if (info) {
        return { ...booking, originInfo: info }
      }
      return booking
    }))
  }

  const getBookingStatus = bookings => {
    getStatus(bookings).then((response) => {
      const updatedBookings = bookings.map(booking => {
        const statusData = response.find(item => item.bookingId === booking.id)
        // Only update if we got status data, otherwise keep the original booking intact
        // /v1/bookings/status may not return all active bookings, so preserve all from input
        if (statusData?.bookingStatus) {
          return {
            ...booking,
            bookingStatus: statusData.bookingStatus
          }
        }
        return booking
      })
      setBookings(updatedBookings)
    })
    .catch((error) => {
      console.error(error.response)
    })
  }

  const loginAndFetchBookings = async userId => {
    const fresh = !isFirstLoadRef.current
    loginUser(userId, fresh)
    getActiveBookings(userId)
    if (fresh) track('login', { userId })
  }


  // Keep ref in sync so async callbacks always see the latest store
  useEffect(() => {
    storeRef.current = store
  }, [store])

  // Write store to local storage when it changes
  useDidMountEffect(() => {
    writeStore(store)
  }, [store])

  // Load user and bookings
  useEffect(() => {
    // If there is a user object (ie. logged in user), update from the API
    if (store?.user?.userId) {
      loginAndFetchBookings(store.user.userId)
    }
    isFirstLoadRef.current = false
  }, [])


  // Handle unauthorized errors
  useCustomEventListener('UNAUTHORIZED', () => {
    const COPY = window.COPY || {}
    const message = COPY.general?.unauthorized_error || 'Du har blivit utloggad. Vänligen logga in igen.'
    const buttonTitle = COPY.booking?.button_ok || 'OK'
    showModal(message, [{ title: buttonTitle, callback: logoutUser }])
  })

  return (
    <StoreContext.Provider value={{
      config, user: store.user, bookings: store.bookings, regions: store.regions, isCompanyTrip: store.isCompanyTrip,
      paymentAccounts: store.paymentAccounts,
      preferredPaymentAccount: store.preferredPaymentAccount,
      preferredPaymentAccountCompany: store.preferredPaymentAccountCompany,
      preferredPaymentMethod: store.preferredPaymentMethod,
      preferredPaymentMethodCompany: store.preferredPaymentMethodCompany,
      currentPaymentMethod: sessionStore.currentPaymentMethod,
      currentPaymentMethodCompany: sessionStore.currentPaymentMethodCompany,
      currentPaymentAccount: sessionStore.currentPaymentAccount,
      currentPaymentAccountCompany: sessionStore.currentPaymentAccountCompany,
      isLoggedIn, loginUser, logoutUser, loginAndFetchBookings, setUser, refreshUser, setPreferredPaymentAccount, setCurrentPaymentAccount, setIsCompanyTrip, getActiveBookings,
      showAddPaymentModal, setShowAddPaymentModal
    }}>
      {apps.map((app) => {
        return createPortal(createElement(app.component, app?.props), app.el)
      })}
      {children}
      {!nested && <MarketingConsentModal />}
      {!nested && <AddPaymentMethodModal />}
      {!nested && <GlobalModal />}
    </StoreContext.Provider>
  )
}

